import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { repostApi, adminApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import PageHeader from '../components/PageHeader';
import { TableScroll, StatGrid } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Repeat,
  Image,
  Video,
  Clock,
  Trash2,
  Upload,
  Settings2,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

/*
 * Reposting & Queue in the Aurora look (2026-08-23). Same data, same handlers, same test ids as
 * before; only the surfaces changed: PageHeader, glass stat tiles (1 -> 2 -> 4 columns), the
 * queue table inside a TableScroll, dashed empty states, and dialogs that fit a 375px phone.
 *
 * Tailwind needs every class spelled out, so the tile / badge tones are full strings. Only
 * 5-step opacity values (10, 15, 20, 25 ...) exist in this build - `/12` would be dropped.
 */
const TONE = {
  primary: 'bg-primary/15 text-primary ring-primary/25',
  success: 'bg-success/15 text-success ring-success/25',
  warning: 'bg-warning/15 text-warning ring-warning/25',
  muted: 'bg-muted/60 text-muted-foreground ring-border',
};

/* One glass stat tile, the Dashboard's shape: icon chip, the value, an uppercase label. */
function Tile({ icon: Icon, tone = 'primary', label, children }) {
  return (
    <div className="aurora-card flex min-w-0 flex-col gap-4 p-4 sm:p-5">
      <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1', TONE[tone] || TONE.primary)}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        {children}
        <p className="mt-2 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function RepostingPage() {
  usePageTitle('Reposting');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: slots } = useQuery({
    queryKey: ['slots'],
    queryFn: () => adminApi.getSlots().then(res => res.data),
  });

  const { data: queue, isLoading: queueLoading } = useQuery({
    queryKey: ['postQueue', selectedSlot],
    queryFn: () => repostApi.getQueue(selectedSlot ? { slot_name: selectedSlot } : {}).then(res => res.data),
    enabled: true,
  });

  const { data: settings } = useQuery({
    queryKey: ['repostSettings', selectedSlot],
    queryFn: () => repostApi.getSettings(selectedSlot).then(res => res.data),
    enabled: !!selectedSlot,
  });

  const uploadMutation = useMutation({
    mutationFn: repostApi.addToQueue,
    onSuccess: () => {
      queryClient.invalidateQueries(['postQueue']);
      setUploadDialogOpen(false);
      setSelectedFile(null);
      toast.success('Post added to queue');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add post');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: repostApi.removeFromQueue,
    onSuccess: () => {
      queryClient.invalidateQueries(['postQueue']);
      toast.success('Post removed from queue');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to remove post');
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: ({ slotName, data }) => repostApi.updateSettings(slotName, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['repostSettings']);
      toast.success('Settings updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadPost = (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedSlot) {
      toast.error('Please select a slot and file');
      return;
    }

    const formData = new FormData();
    formData.append('slot_name', selectedSlot);
    formData.append('media_type', selectedFile.type.startsWith('video/') ? 'VIDEO' : 'IMAGE');
    formData.append('caption', e.target.caption.value || '');
    formData.append('media', selectedFile);

    if (e.target.schedule_time.value) {
      formData.append('schedule_time', e.target.schedule_time.value);
    }

    uploadMutation.mutate(formData);
  };

  const handleTogglePosting = () => {
    if (!selectedSlot) return;
    updateSettingsMutation.mutate({
      slotName: selectedSlot,
      data: { is_posting_enabled: !settings?.is_posting_enabled },
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: { color: 'bg-warning/15 text-warning border-warning/30', icon: Clock },
      SCHEDULED: { color: 'bg-primary/15 text-primary border-primary/30', icon: Calendar },
      POSTED: { color: 'bg-success/15 text-success border-success/30', icon: CheckCircle },
      FAILED: { color: 'bg-danger/15 text-danger border-danger/30', icon: AlertCircle },
    };
    const v = variants[status] || variants.PENDING;
    const Icon = v.icon;
    return (
      <Badge className={`${v.color} border rounded-full whitespace-nowrap`} data-testid="post-status-badge">
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const getMediaIcon = (type) => {
    return type === 'VIDEO' ? (
      <Video className="h-4 w-4 text-accent" />
    ) : (
      <Image className="h-4 w-4 text-primary" />
    );
  };

  return (
    <div className="relative z-[1] space-y-6" data-testid="reposting-page">
      {/* Header */}
      <PageHeader
        icon={Repeat}
        title="Reposting & Queue"
        description="Manage your post queue and reposting settings"
        actions={(
          <>
            <Select value={selectedSlot || ''} onValueChange={setSelectedSlot}>
              <SelectTrigger className="tap w-full xs:w-[200px] bg-secondary/60 border-border" data-testid="slot-select">
                <SelectValue placeholder="Select slot" />
              </SelectTrigger>
              <SelectContent>
                {slots?.map((slot) => (
                  <SelectItem key={slot.slot_name} value={slot.slot_name}>
                    {slot.slot_label || slot.slot_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSlot && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setSettingsDialogOpen(true)}
                  className="tap"
                  data-testid="settings-button"
                >
                  <Settings2 className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                <Button
                  onClick={() => setUploadDialogOpen(true)}
                  className="tap shadow-glow"
                  data-testid="upload-post-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Post
                </Button>
              </>
            )}
          </>
        )}
      />

      {/* Stats Cards */}
      {selectedSlot && (
        <StatGrid cols={4}>
          <Tile icon={Repeat} tone="primary" label="Posting Status">
            <div className="flex items-center gap-2">
              <Switch
                checked={settings?.is_posting_enabled || false}
                onCheckedChange={handleTogglePosting}
                data-testid="posting-toggle"
              />
              <span className={cn('text-sm font-semibold', settings?.is_posting_enabled ? 'text-success' : 'text-muted-foreground')}>
                {settings?.is_posting_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </Tile>
          <Tile icon={Clock} tone="warning" label="Queued Posts">
            <p className="truncate font-heading text-2xl font-semibold leading-none tracking-tight tnum sm:text-[1.75rem]" data-testid="queued-count">
              {queue?.filter(p => ['PENDING', 'SCHEDULED'].includes(p.status)).length || 0}
            </p>
          </Tile>
          <Tile icon={CheckCircle} tone="success" label="Posts Today">
            <p className="truncate font-heading text-2xl font-semibold leading-none tracking-tight tnum text-success sm:text-[1.75rem]" data-testid="posts-today">
              {settings?.posts_posted_count || 0}
            </p>
          </Tile>
          <Tile icon={FileText} tone="muted" label="Daily Limit">
            <p className="truncate font-heading text-2xl font-semibold leading-none tracking-tight tnum sm:text-[1.75rem]" data-testid="daily-limit">
              {settings?.posts_per_day_min || 1}-{settings?.posts_per_day_max || 5}
            </p>
          </Tile>
        </StatGrid>
      )}

      {/* Queue Table */}
      <Card className="aurora-card">
        <CardHeader>
          <CardTitle className="font-heading text-base font-semibold">Post Queue</CardTitle>
          <CardDescription>
            {selectedSlot ? `Posts scheduled for ${selectedSlot}` : 'Select a slot to view its post queue'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedSlot ? (
            <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10">
              <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                <Repeat className="h-6 w-6" />
              </span>
              <h3 className="mt-4">Select a Slot</h3>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Choose a slot from the dropdown to manage its post queue</p>
            </div>
          ) : queueLoading ? (
            <div className="space-y-2" aria-busy="true" aria-label="Loading post queue">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary/60" />
              ))}
            </div>
          ) : queue && queue.length > 0 ? (
            <TableScroll>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Type</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Caption</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.map((post) => (
                    <TableRow key={post._id} className="border-border" data-testid="post-row">
                      <TableCell>{getMediaIcon(post.media_type)}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-sm" title={post.media_filename || undefined}>
                        {post.media_filename || 'Unknown'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={post.caption || undefined}>{post.caption || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap tnum">
                        {post.schedule_time
                          ? new Date(post.schedule_time).toLocaleString()
                          : 'ASAP'
                        }
                      </TableCell>
                      <TableCell>{getStatusBadge(post.status)}</TableCell>
                      <TableCell className="text-right">
                        {['PENDING', 'SCHEDULED'].includes(post.status) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteMutation.mutate(post._id)}
                            className="tap h-8 w-8 text-danger hover:text-danger hover:bg-danger/10"
                            aria-label="Remove post from queue"
                            data-testid="post-delete-button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScroll>
          ) : (
            <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10">
              <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                <Image className="h-6 w-6" />
              </span>
              <h3 className="mt-4">No posts in queue</h3>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Add posts to the queue to start scheduling</p>
              <Button
                onClick={() => setUploadDialogOpen(true)}
                className="tap mt-5"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Post
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Post Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-card border-border w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Post to Queue</DialogTitle>
            <DialogDescription>
              Upload an image or video to add to the post queue.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadPost} className="space-y-4">
            <div className="space-y-2">
              <Label>Media File</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                className="tap rounded-2xl border-2 border-dashed border-border bg-secondary/30 p-6 sm:p-8 text-center cursor-pointer hover:border-primary/40 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="media-file-input"
                />
                {selectedFile ? (
                  <div className="flex min-w-0 items-center justify-center gap-2">
                    {selectedFile.type.startsWith('video/') ? (
                      <Video className="h-6 w-6 shrink-0 text-accent" />
                    ) : (
                      <Image className="h-6 w-6 shrink-0 text-primary" />
                    )}
                    <span className="min-w-0 truncate font-medium">{selectedFile.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to upload image or video</p>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="caption">Caption</Label>
              <Textarea
                id="caption"
                name="caption"
                placeholder="Write your post caption..."
                className="bg-secondary/60 border-border min-h-[100px]"
                data-testid="post-caption-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule_time">Schedule Time (optional)</Label>
              <Input
                id="schedule_time"
                name="schedule_time"
                type="datetime-local"
                className="bg-secondary/60 border-border"
                data-testid="post-schedule-input"
              />
              <p className="text-xs text-muted-foreground">Leave empty to post as soon as possible</p>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="outline" className="tap" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="tap"
                disabled={uploadMutation.isPending || !selectedFile}
                data-testid="upload-submit-button"
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Add to Queue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-card border-border w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Repost Settings</DialogTitle>
            <DialogDescription>
              Configure posting settings for {selectedSlot}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label>Enable Posting</Label>
                <p className="text-xs text-muted-foreground mt-1">Allow automated posting for this slot</p>
              </div>
              <Switch
                checked={settings?.is_posting_enabled || false}
                onCheckedChange={(checked) =>
                  updateSettingsMutation.mutate({
                    slotName: selectedSlot,
                    data: { is_posting_enabled: checked }
                  })
                }
                data-testid="settings-posting-toggle"
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label>Auto Repost</Label>
                <p className="text-xs text-muted-foreground mt-1">Automatically repost content from sources</p>
              </div>
              <Switch
                checked={settings?.repost_enabled || false}
                onCheckedChange={(checked) =>
                  updateSettingsMutation.mutate({
                    slotName: selectedSlot,
                    data: { repost_enabled: checked }
                  })
                }
                data-testid="settings-repost-toggle"
              />
            </div>

            <div className="space-y-3">
              <Label>Post Interval (minutes)</Label>
              <div className="flex items-center gap-4">
                <Slider
                  defaultValue={[settings?.post_interval_minutes || 60]}
                  min={15}
                  max={360}
                  step={15}
                  onValueCommit={([value]) =>
                    updateSettingsMutation.mutate({
                      slotName: selectedSlot,
                      data: { post_interval_minutes: value }
                    })
                  }
                  className="flex-1"
                  data-testid="settings-interval-slider"
                />
                <span className="w-16 shrink-0 text-right text-sm font-mono tnum">
                  {settings?.post_interval_minutes || 60} min
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Posts/Day</Label>
                <Input
                  type="number"
                  defaultValue={settings?.posts_per_day_min || 1}
                  min={1}
                  max={20}
                  className="bg-secondary/60 border-border"
                  onBlur={(e) =>
                    updateSettingsMutation.mutate({
                      slotName: selectedSlot,
                      data: { posts_per_day_min: parseInt(e.target.value) }
                    })
                  }
                  data-testid="settings-min-posts-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Posts/Day</Label>
                <Input
                  type="number"
                  defaultValue={settings?.posts_per_day_max || 5}
                  min={1}
                  max={20}
                  className="bg-secondary/60 border-border"
                  onBlur={(e) =>
                    updateSettingsMutation.mutate({
                      slotName: selectedSlot,
                      data: { posts_per_day_max: parseInt(e.target.value) }
                    })
                  }
                  data-testid="settings-max-posts-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Caption Template (Spintax)</Label>
              <Textarea
                defaultValue={settings?.caption_spintax || ''}
                placeholder="{Check out|Don't miss} this {amazing|awesome} content! #hashtag"
                className="bg-secondary/60 border-border min-h-[80px]"
                onBlur={(e) =>
                  updateSettingsMutation.mutate({
                    slotName: selectedSlot,
                    data: { caption_spintax: e.target.value }
                  })
                }
                data-testid="settings-caption-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="tap" onClick={() => setSettingsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
